

import { DateRange, Search } from '@mui/icons-material';
import { Box, Button, FormLabel, MenuItem } from '@mui/material';
import _ from 'lodash';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ApplicationMimeTypesEnum } from '../../common/enums/ApplicationMimeTypesEnum';
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { ActionTypeEnum, StatusEnum } from '../../common/IExportNotificationMsg';
import UUID from "../../common/utilities/Uuid";
import { ActionMenuItem, BaseActionEnum } from '../../components/action-menu/ActionMenuItem';
import { ContentContainer } from '../../components/content-container/ContentContainer';
import { ContentSection } from '../../components/content-section/ContentSection';
import { ContentView } from '../../components/content-view/ContentView';
import ContextualAppBar from '../../components/contextual-app-bar/ContextualAppBar';
import { bindCustomDurationDialog, CustomDurationDialog, CustomDurationDialogValues, useCustomDurationDialogState } from '../../components/custom-duration-dialog/CustomDurationDialog';
import LoadingZone from '../../components/loading-zone/LoadingZone';
import NoData from '../../components/no-data/NoData';
import OptionsSideSheet from '../../components/options-side-sheet/OptionsSideSheet';
import SideSheetFilterInput from '../../components/side-sheet/side-sheet-filter-input/SideSheetFilterInput';
import SideSheetSelect from '../../components/side-sheet/side-sheet-select/SideSheetSelect';
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import { useProgressContext } from '../../contexts/progress-context/ProgressContext';
import { useRouteContext } from '../../hooks/use-route-context/UseRouteContext';
import AuditReportingDataService, { IAuditTrailFilter } from '../../services/audit-reporting-data-service/AuditReportingDataService';
import { AuditActionEnum } from '../../services/audit-reporting-data-service/Models';
import { RestError } from "../../services/rest-service/RestService";
import AuditFilterDialog, { bindAuditFilterDialog, useAuditFilterDialogState } from './audit-filter-dialog/AuditFilterDialog';
import useAuditReportingState from './AuditReportingState';
import AuditReportingUtils from './AuditReportingUtils';
import DefaultGrid from "../../components/default-grid/DefaultGrid";

//const BUTTON_PULSE_TIMEOUT: number = 10000;

export default function AuditReportingView() {

    const routeContext = useRouteContext();
    const { t } = useTranslation();
    const [, setAppNotificationState] = useAppNotificationContext();
    const durationDialog = useCustomDurationDialogState();
    const auditFilterDialog = useAuditFilterDialogState();
    const [progressState, actionStarted] = useProgressContext();

    const {
        state,
        setData,
        setRun,
        setDuration,
        setSearchText,
        setActions,
        setObjectName,
        setObjectType,
        setUserName,
        setAuditAction,
        setOldValue,
        setNewValue,
        setMaxRows,
        setAdditionalInfo,
        resetState,
        setLoading,
        setDownloadUid
    } = useAuditReportingState();

    // const {data: auditEntries, status, refetch} = useQueryNotifications(useAuditReporting)({
    //     groupID: routeContext.groupID as number,
    //     startDate: state.durationOptions.duration.startDate,
    //     endDate: state.durationOptions.duration.endDate,
    //     auditAction: state.auditAction,
    //     objectType: state.objectType,
    //     userName: state.userName,
    //     objectName: state.objectName,
    //     oldValue: state.oldValue,
    //     newValue: state.newValue,
    //     searchText: state.searchText,
    //     maxRows: state.maxRows,
    //     enabled: state.run
    // });


    const noDataTitle = t(!state.run ? 'actions.runReport' : 'noData.title');
    const noDataText = state.run && state.data !== undefined && state.data.rows.length === 0 ?
        t('auditReporting.noData') :
        !state.run ?
            t('auditReporting.runReportHint') :
            undefined;

    const updateActions = useCallback(() => {

        const actions = _.cloneDeep(state.actions);
        const runAction = actions.find(o => o.action === BaseActionEnum.Run);
        if (runAction && runAction.pulse) {

            runAction.pulse = false;
            setActions(actions);
        }
    }, [
        state.actions,
        setActions,
    ]);


    const onExport = (mimeType: ApplicationMimeTypesEnum) => {


        const uid = UUID.generate();
        setDownloadUid(uid);

        actionStarted({
            uid: uid,
            objectID: routeContext.groupID as number,
            objectType: eLynxObjectTypeEnum.Group,
            title: 'Audit Report',
            notificationMsg: {
                action: ActionTypeEnum.Download,
                notificationID: uid,
                status: StatusEnum.Started,
                statusMessage: t('progress.sendingRequest'),
                additionalInfo: '',
                blobURI: '',
                percentComplete: 0
            }
        });

        try {

            AuditReportingDataService.exportData(routeContext.groupID as number,
                uid,
                mimeType,
                AuditReportingUtils.mapToAuditFilter(state));

            const items = [...state.actions];
            const exportAction = items.find(o => o.action === BaseActionEnum.Export);
            if (exportAction) {
                exportAction.disabled = true;
            }
            setActions(items);
        } catch (error) {
            const restError = error as RestError;
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                show: true,
                error: restError
            });
        }
    };


    async function onCustomDuration() {
        const result = await durationDialog.open(state.durationOptions);

        if (result.reason === "ok") {
            const values = result.values as CustomDurationDialogValues;

            setDuration(values);
        }
    }

    async function onRunReport() {
        _runReport(AuditReportingUtils.mapToAuditFilter(state));
    }

    const _runReport = useCallback(async (filter: IAuditTrailFilter) => {

        try {
            setRun(true);
            setLoading(true);

            const auditEntries = await AuditReportingUtils.getData(routeContext.groupID as number, filter);

            const cols = AuditReportingUtils.createGridColumns();
            const rows = AuditReportingUtils.createGridRows(auditEntries);

            setData({
                cols,
                rows
            });

        } catch (error) {

            const err = error as RestError;

            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                message: err.message,
                show: true,
                error: err
            });

        }
        finally {
            setLoading(false);
        }

        updateActions();
    }, [
        routeContext.groupID,
        setRun,
        setLoading,
        setData,
        setAppNotificationState,
        updateActions
    ]);

    // async function _xrunReport(filter: IAuditTrailFilter) {
    //     try {
    //         setRun(true);
    //         setLoading(true);

    //         const auditEntries = await AuditReportingUtils.getData(routeContext.groupID as number,filter);

    //         const cols = AuditReportingUtils.createGridColumns();
    //         const rows = AuditReportingUtils.createGridRows(auditEntries);

    //         setData({
    //             cols,
    //             rows
    //         });

    //     } catch (error) {

    //         const err = error as RestError;

    //         setAppNotificationState({
    //             notificationType: AppNotificationTypeEnum.Error,
    //             message: err.message,
    //             show: true,
    //             error: err
    //         });

    //     }
    //     finally {
    //         setLoading(false);
    //     }

    //     updateActions();
    // }

    function onExecuteAction(menuItem: ActionMenuItem) {


        switch (menuItem.action) {
            case BaseActionEnum.Refresh:
                onRunReport();
                break;

            case BaseActionEnum.Run:
                onRunReport();
                break;

            case BaseActionEnum.PDF:
                onExport(ApplicationMimeTypesEnum.PDF);
                break;

            case BaseActionEnum.Excel:
                onExport(ApplicationMimeTypesEnum.MSExcel);
                break;
        }
    }

    function onResetOptions() {
        resetState();
    }

    const onAdvancedSearch = useCallback(async () => {

        const result = await auditFilterDialog.open({

            startDate: state.durationOptions.duration.startDate,
            endDate: state.durationOptions.duration.endDate,
            objectType: state.objectType ?? eLynxObjectTypeEnum.None,
            objectName: state.objectName,
            action: state.auditAction ?? AuditActionEnum.None,
            userLogin: state.userName,
            oldValue: state.oldValue,
            newValue: state.newValue,
            maxRows: state.maxRows
        });
        if (result.reason === 'ok') {
            if (result.values) {
                setObjectType(result.values.objectType !== eLynxObjectTypeEnum.None ? result.values.objectType : undefined);
                setObjectName(result.values.objectName);
                setUserName(result.values.userLogin);
                setAuditAction(result.values.action !== AuditActionEnum.None ? result.values.action : undefined);
                setOldValue(result.values.oldValue);
                setNewValue(result.values.newValue);
                setAdditionalInfo(result.values.additionalInfo);

                _runReport(result.values);
            }
        }
    }, [
        auditFilterDialog,
        state,
        _runReport,
        setObjectName,
        setObjectType,
        setUserName,
        setAuditAction,
        setOldValue,
        setNewValue,
        setAdditionalInfo
    ]);

    // useEffect(() => {
    //     const runAction = state.actions.find(o => o.action === AuditReportingActionEnum.Run);
    //     if (runAction && runAction.pulse) {
    //         window.setTimeout(() => {
    //             updateActions();
    //         }, BUTTON_PULSE_TIMEOUT);
    //     }
    // },[
    //     state.actions,
    //     updateActions
    // ]);


    // get progress running for current object
    const progress = progressState.items.find(o => o.objectID === routeContext.groupID && o.objectType === eLynxObjectTypeEnum.Group);

    useEffect(() => {

        if (progress?.uid === state.downloadUid &&
            (progress?.notificationMsg.status === StatusEnum.Completed ||
                progress?.notificationMsg.status === StatusEnum.Failed)) {
            setDownloadUid(undefined);
        }
    }, [
        progress,
        state.downloadUid,
        setDownloadUid
    ]);

    useEffect(() => {

        const items = _.cloneDeep(state.actions);

        const exportAction = items.find(o => o.action === BaseActionEnum.Export);

        if (exportAction) {
            exportAction.disabled = progress && progress.uid === state.downloadUid;
        }

        // if (!_.isEqual(state.actions, items)) {
            setActions(items);
        // }

    }, [
        state.actions,
        progress,
        state.downloadUid,
        setActions
    ]);


    return (
        <ContentView>
            <ContextualAppBar
                hideBreadcrumb={true}
                actions={state.actions}
                viewOptionState={state.viewOptionState}
                onExecuteAction={onExecuteAction}
            //showOrganizationPicker={true} // KG: 2022-11-01 org picker should be hidden for water customers.
            // since this is the only functionality here we do not need additional logic to handle.  If/when
            // support for other devices in oil/gas customers is added then logic can be added.
            //onOrganizationSelected={onOrganizationSelected} 
            //organizationID={state.organizationID}
            />

            <LoadingZone isLoading={state.loading}>
                <ContentContainer>
                    <ContentSection>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                flexGrow: 1,
                                margin: '1rem',
                                overflow: "hidden"
                            }}>

                            {noDataText &&
                                <NoData
                                    title={noDataTitle}
                                    text={noDataText}
                                />
                            }

                            {state.run && state.data && state.data.rows.length > 0 &&


                                <DefaultGrid
                                    suppressColumnVirtualisation={true}
                                    suppressMultiSort={true}
                                    rowData={state.data.rows}
                                    defaultColDef={{
                                        sortable: false,
                                        filter: "agMultiColumnFilter",
                                        resizable: true,
                                    }}
                                    columnDefs={state.data.cols}
                                    // SPR - Fix "AG Grid: warning #25 The getRowId callback must return a string."
                                    getRowId={data => `${data.data.id}`}
                                />
                            }
                        </Box>

                    </ContentSection>
                    <OptionsSideSheet onReset={onResetOptions}>
                        <SideSheetFilterInput
                            label={t("auditReporting.searchText")}
                            value={state.searchText}
                            onChange={(value) => {
                                setSearchText(value);
                            }}
                        />

                        <FormLabel component="legend">{t('customDuration.duration')}</FormLabel>
                        <Button variant='text'
                            startIcon={<DateRange />}
                            sx={{ justifyContent: 'flex-start' }}
                            onClick={onCustomDuration}>
                            {state.durationLabel}
                        </Button>

                        <SideSheetSelect
                            label={t("auditReporting.maxRows")}
                            value={state.maxRows.toString()}
                            onChange={(value) => {
                                setMaxRows(+value);
                            }}>
                            <MenuItem value="500">
                                500
                            </MenuItem>
                            <MenuItem value="1000">
                                1,000
                            </MenuItem>
                            <MenuItem value="2500">
                                2,500
                            </MenuItem>
                            <MenuItem value="5000">
                                5,000
                            </MenuItem>
                            <MenuItem value="10000">
                                10,000
                            </MenuItem>
                        </SideSheetSelect>

                        <Button variant='text'
                            startIcon={<Search />}
                            disabled={state.searchText.length > 0}
                            sx={{ justifyContent: 'flex-start' }}
                            onClick={onAdvancedSearch}>
                            {t('auditReporting.advancedSearch')}
                        </Button>
                    </OptionsSideSheet>
                </ContentContainer>
            </LoadingZone>

            <CustomDurationDialog
                hideDefault={true}
                value={state.durationOptions}
                {...bindCustomDurationDialog(durationDialog)}
            />

            <AuditFilterDialog {...bindAuditFilterDialog(auditFilterDialog)} />

        </ContentView>
    );
}
